import React, { useEffect, useMemo, useState } from "react";
import { CalendarDays, Loader2 } from "lucide-react";
import { api } from "../lib/api";

// Sequential green-amber-red palette. `pct` = % of squad DEFICIENT in the
// (month, marker) cell — higher = worse.
function pctColor(pct, n) {
  if (n < 3) return { bg: "#1E293B", text: "#64748B" };
  if (pct === 0) return { bg: "#065F46", text: "#D1FAE5" };
  if (pct < 25) return { bg: "#166534", text: "#DCFCE7" };
  if (pct < 50) return { bg: "#B45309", text: "#FEF3C7" };
  if (pct < 75) return { bg: "#B91C1C", text: "#FEE2E2" };
  return { bg: "#7F1D1D", text: "#FECACA" };
}

const MONTH_LABEL = (ym) => {
  // "2025-06" -> "Jun 25"
  const [y, m] = ym.split("-");
  const mm = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][parseInt(m, 10) - 1];
  return `${mm} ${y.slice(-2)}`;
};

/**
 * Group consecutive months into seasons for the heatmap header row.
 * Indian (Hyderabad) climate — the coach asked for the season labels
 * to be visible above the month columns so a spike in April/May
 * (summer) or June/September (monsoon) is spotted at a glance.
 *
 * Season → months mapping:
 *   Winter   → Dec, Jan, Feb
 *   Summer   → Mar, Apr, May
 *   Monsoon  → Jun, Jul, Aug, Sep
 *   Post-mon → Oct, Nov
 */
function _monthToSeason(monthNum) {
  if (monthNum === 12 || monthNum === 1 || monthNum === 2) return "Winter";
  if (monthNum >= 3 && monthNum <= 5) return "Summer";
  if (monthNum >= 6 && monthNum <= 9) return "Monsoon";
  return "Post-monsoon";
}

function _seasonSpans(monthStrs) {
  const spans = [];
  for (const m of monthStrs) {
    const mm = parseInt(m.split("-")[1], 10);
    const season = _monthToSeason(mm);
    const last = spans[spans.length - 1];
    if (last && last.season === season) last.count += 1;
    else spans.push({ season, count: 1 });
  }
  return spans;
}

const _SEASON_TONE = {
  "Winter":       "text-sky-300",
  "Summer":       "text-amber-300",
  "Monsoon":      "text-emerald-300",
  "Post-monsoon": "text-orange-300",
};

/**
 * Squad Seasonal Heatmap — 12+ months × marker grid showing % deficient
 * across the squad in each cell. Turns time into a supplementation planning
 * tool: reveals monsoon Vit D drops, pre-nationals iron crashes etc.
 */
export default function SquadSeasonalHeatmap({ parameters }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const paramByKey = useMemo(
    () => Object.fromEntries((parameters || []).map((p) => [p.key, p])),
    [parameters],
  );

  useEffect(() => {
    setLoading(true);
    api.get("/squad/seasonal-heatmap")
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  // Only show markers where we actually have data. Sort by total tests
  // descending so the most-monitored markers are top of the grid.
  const markers = useMemo(() => {
    if (!data) return [];
    return Object.entries(data.cells)
      .map(([k, cells]) => ({
        key: k,
        total: Object.values(cells).reduce((a, c) => a + c.n, 0),
      }))
      .sort((a, b) => b.total - a.total)
      .map((x) => x.key);
  }, [data]);

  return (
    <div className="card p-5 md:p-6" data-testid="squad-seasonal-heatmap">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-400 flex items-center gap-1.5">
            <CalendarDays size={12} className="text-amber-300" /> Seasonal Deficiency Heatmap
          </div>
          <div className="font-display text-lg text-slate-100 mt-0.5">
            Month × marker · % of squad deficient (low)
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            Time-of-year patterns — plan supplementation calendar against the curve. Cells with fewer
            than 3 tests are hidden as noise.
          </div>
        </div>
        <div className="text-[10px] font-mono-data text-slate-400 flex items-center gap-2">
          <div className="flex items-center gap-0.5">
            <span className="inline-block w-3 h-3 rounded" style={{ background: "#065F46" }} /> 0%
            <span className="inline-block w-3 h-3 rounded" style={{ background: "#166534" }} /> &lt;25
            <span className="inline-block w-3 h-3 rounded" style={{ background: "#B45309" }} /> &lt;50
            <span className="inline-block w-3 h-3 rounded" style={{ background: "#B91C1C" }} /> &lt;75
            <span className="inline-block w-3 h-3 rounded" style={{ background: "#7F1D1D" }} /> ≥75
          </div>
        </div>
      </div>

      {loading ? (
        <div className="py-8 text-center text-slate-500 text-sm">
          <Loader2 size={14} className="animate-spin inline mr-2" /> Loading…
        </div>
      ) : !data || !markers.length ? (
        <div className="rounded-lg border border-dashed border-slate-700 p-6 text-center text-sm text-slate-400">
          No seasonal data yet — need at least 3 tests per month per marker.
        </div>
      ) : (
        <div className="overflow-auto" style={{ maxHeight: 520 }}>
          <table className="w-full text-[10px]">
            <thead className="sticky top-0 bg-[#0B1627] z-10">
              {/* Season header row — groups adjacent month columns and
                  makes seasonal trends visible at a glance. */}
              <tr>
                <th className="sticky left-0 bg-[#0B1627] z-20" />
                {_seasonSpans(data.months).map((sp, i) => (
                  <th
                    key={`${sp.season}-${i}`}
                    colSpan={sp.count}
                    className={`px-1 pt-2 pb-0.5 text-center font-normal uppercase tracking-widest text-[9px] ${_SEASON_TONE[sp.season] || "text-slate-400"} border-b border-[#1E304B]`}
                    data-testid={`seasonal-band-${sp.season.toLowerCase()}-${i}`}
                  >
                    {sp.season}
                  </th>
                ))}
              </tr>
              <tr>
                <th className="text-left px-2 py-2 text-slate-400 font-normal uppercase tracking-wider sticky left-0 bg-[#0B1627] z-20 min-w-[110px]">
                  Marker
                </th>
                {data.months.map((m) => (
                  <th key={m} className="px-1 py-2 text-center text-slate-400 font-normal uppercase tracking-wider whitespace-nowrap">
                    {MONTH_LABEL(m)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {markers.map((k) => {
                const p = paramByKey[k];
                return (
                  <tr key={k} className="border-t border-[#1E304B]">
                    <td className="px-2 py-1.5 text-slate-200 font-medium sticky left-0 bg-[#0B1627] z-10">
                      {p?.label || k.toUpperCase()}
                    </td>
                    {data.months.map((m) => {
                      const cell = data.cells[k]?.[m];
                      if (!cell) {
                        return <td key={m} style={{ background: "#0B1627" }} />;
                      }
                      const style = pctColor(cell.pct, cell.n);
                      return (
                        <td
                          key={m}
                          className="px-1 py-1.5 text-center font-mono-data leading-tight"
                          style={{ background: style.bg, color: style.text }}
                          title={`${p?.label || k} · ${MONTH_LABEL(m)} · ${cell.def}/${cell.n} deficient (${cell.pct}%)`}
                          data-testid={`heatmap-${k}-${m}`}
                        >
                          {cell.pct === 0 ? "·" : `${Math.round(cell.pct)}`}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
